/**
 * Der Konzernanhang (2.15) — Anhangsbuch, Angabenkatalog, Vollständigkeit.
 *
 * **Der Bildschirm, um den es geht, ist das Anhangsbuch**, und er ist
 * dreispaltig: links die Gliederung mit Zustandssymbolen, in der Mitte die
 * Angabe als Text, rechts die Herkunft jeder Zahl im Absatz. Die mittlere
 * Spalte ist ein Text — das ist der Unterschied zu jedem anderen Bildschirm
 * dieses Programms, und alles Weitere folgt daraus.
 *
 * **Die tragende Stelle ist `Satz`.** Im Anhangstext steht keine Zahl, sondern
 * eine Marke `[[a1]]` (R220). Erst hier wird daraus ein Betrag — sichtbar als
 * Zahl aus dem System, nicht als getippter Text, und anklickbar bis in den
 * Wertenachweis (R222). Wer diese Funktion versteht, versteht den Bereich.
 */

import { useEffect, useState } from 'react'
import { api } from './api'
import { Zustandssymbol } from './Zustand'
import type { Zustandsart } from './Zustand'
import { anzahlwort, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type {
  Angabenart,
  Angabenkatalogeintrag,
  Angabenherkunft,
  Angabenzustand,
  Anhangsbuch as Buch,
  Ankerwert,
  Buchangabe,
  Gliederungsgruppe,
  Vollstaendigkeitsbefund,
} from './typen'

/* -------------------------------------------------------------------------
   Die Übersetzung der Aufzählungen.

   Ausgeschrieben und nicht aus dem Wert zusammengesetzt: So bricht ein
   Tippfehler beim Übersetzen ab, statt in der englischen Oberfläche einen
   deutschen Begriff stehen zu lassen (dieselbe Haltung wie in `bereiche.tsx`).
   ------------------------------------------------------------------------- */

const ZUSTAND: Record<Angabenzustand, Zustandsart> = {
  Fehlt: 'fehlt',
  Offen: 'offen',
  Blockiert: 'blockiert',
  Veraltet: 'veraltet',
  Fertig: 'fertig',
}

const GRUPPEN: { id: Gliederungsgruppe; schluessel: Sprachschluessel }[] = [
  { id: 'Allgemeines', schluessel: 'anhang.gruppe.allgemeines' },
  { id: 'Methoden', schluessel: 'anhang.gruppe.methoden' },
  { id: 'Bilanz', schluessel: 'anhang.gruppe.bilanz' },
  { id: 'Guv', schluessel: 'anhang.gruppe.guv' },
  { id: 'Anteilsbesitz', schluessel: 'anhang.gruppe.anteilsbesitz' },
  { id: 'Sonstiges', schluessel: 'anhang.gruppe.sonstiges' },
]

const HERKUNFT: Record<Angabenherkunft, Sprachschluessel> = {
  Gerechnet: 'anhang.herkunft.gerechnet',
  Erhoben: 'anhang.herkunft.erhoben',
  Erklaert: 'anhang.herkunft.erklaert',
}

const ART: Record<Angabenart, Sprachschluessel> = {
  Pflicht: 'anhang.art.pflicht',
  Wahlrecht: 'anhang.art.wahlrecht',
  Freiwillig: 'anhang.art.freiwillig',
}

/**
 * Der Zustand, wie die Gliederung ihn zeigt.
 *
 * Eine Fehlanzeige ist **erledigt** (R230) und trägt deshalb das Symbol von
 * „fertig" — sie ist eine getroffene Feststellung und kein offener Punkt. Dass
 * sie eine ist, sagt der Text daneben.
 */
function zustandssymbolart(a: Buchangabe): Zustandsart {
  return a.istFehlanzeige ? 'fertig' : ZUSTAND[a.zustand]
}

/* -------------------------------------------------------------------------
   Der Satz mit seinen Zahlen — das Kernstück
   ------------------------------------------------------------------------- */

/** `[[a1]]` — dieselbe Marke wie in `core.angabentext`. */
const MARKE = /\[\[([a-z][a-z0-9]{0,15})\]\]/g

/**
 * Ein Anhangstext, in dem die Marken durch ihre Beträge ersetzt sind.
 *
 * **Die Zahl ist sichtbar eine Zahl aus dem System** (2.15, Abschnitt 6.3):
 * Sie steht in Klammern und in tabellarischen Ziffern. Wer sie überschreiben
 * will, merkt, dass sie kein Text ist.
 *
 * **Ein Anker, der sich nicht auflöst, wird nicht zu null.** Er zeigt, dass er
 * nicht auflösbar ist — eine stillschweigende Null wäre die gefährlichste
 * Antwort, die dieser Bildschirm geben könnte.
 */
function Satz({
  text,
  anker,
  aufNachweis,
}: {
  text: string
  anker: Ankerwert[]
  aufNachweis: (w: Ankerwert) => void
}) {
  const teile: React.ReactNode[] = []
  let zuletzt = 0
  let lauf = 0

  for (const treffer of text.matchAll(MARKE)) {
    const beginn = treffer.index ?? 0
    if (beginn > zuletzt) {
      teile.push(text.slice(zuletzt, beginn))
    }

    const wert = anker.find((a) => a.marke === treffer[1])
    lauf += 1

    if (!wert) {
      // Eine Marke ohne Anker. Das ist ein Pflegefehler und wird gezeigt, statt
      // die Marke stehen zu lassen: Wer `[[a3]]` im Ausdruck liest, hält es für
      // einen Programmfehler.
      teile.push(
        <span className="ankerfehler" key={`f${lauf}`}>
          {t('anhang.anker.nichtaufloesbar')}
        </span>,
      )
    } else if (wert.dargestellt === null) {
      teile.push(
        <span className="ankerfehler" key={`f${lauf}`}>
          {t('anhang.anker.nichtaufloesbar')}
        </span>,
      )
    } else {
      teile.push(
        <button
          type="button"
          className="anker"
          key={`a${lauf}`}
          onClick={() => aufNachweis(wert)}
          // R222: Der Anker ist eine Wertzelle in einem Satz und erbt den
          // Wertenachweis. Der Titel sagt das, bevor jemand klickt.
          title={`${wert.position ?? wert.konto ?? t('anhang.anker.auszulieferung')} — ${t('anhang.anker.nachweis')}`}
        >
          {wert.dargestellt}
        </button>,
      )
    }

    zuletzt = beginn + treffer[0].length
  }

  if (zuletzt < text.length) {
    teile.push(text.slice(zuletzt))
  }

  return <p className="anhangssatz">{teile}</p>
}

/* -------------------------------------------------------------------------
   Das Anhangsbuch
   ------------------------------------------------------------------------- */

export function Anhangsbuch({
  kreis,
  ledger,
  periode,
  sprache,
  aufNachweis,
}: {
  kreis: string
  ledger: string
  periode: number
  sprache: string
  /** Führt in den Wertenachweis — R222, dieselbe Herleitung wie im Bericht. */
  aufNachweis: (ziel: {
    art: 'bilanz' | 'guv'
    position: string
    positionsname: string
    konto: string | null
    einheit: string | null
    spalte: string
  }) => void
}) {
  const [buch, setBuch] = useState<Buch | null>(null)
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)

  useEffect(() => {
    let gilt = true
    api.anhangsbuch(kreis, ledger, periode).then((b) => {
      if (!gilt) return
      setBuch(b)
      // Der Einstieg ist die erste Angabe, an der es etwas zu sehen gibt --
      // nicht die erste der Gliederung, die in aller Regel noch leer ist.
      const ersteMitInhalt = b.angaben.find((a) => Object.keys(a.texte).length > 0)
      setGewaehlt((ersteMitInhalt ?? b.angaben[0])?.schluessel ?? null)
    })
    return () => {
      gilt = false
    }
  }, [kreis, ledger, periode])

  if (!buch) {
    return null
  }

  const angabe = buch.angaben.find((a) => a.schluessel === gewaehlt) ?? null
  const offen = buch.angaben.filter((a) => !a.istFehlanzeige && a.zustand !== 'Fertig').length
  const hart = buch.befunde.filter((b) => b.haerte === 'Hart').length

  return (
    <div className="anhangsbuch">
      {/* --- Links: die Gliederung (L2★, dieselbe Baumbedienung wie der
          Positionsbaum) --- */}
      <nav className="anhangsgliederung" aria-label={t('anhang.gliederung')}>
        <h2 className="abschnitt">{t('anhang.gliederung')}</h2>

        {GRUPPEN.map((gruppe) => {
          const drin = buch.angaben.filter((a) => a.gruppe === gruppe.id)
          if (drin.length === 0) return null

          return (
            <section key={gruppe.id}>
              <h3>{t(gruppe.schluessel)}</h3>
              <ul>
                {drin.map((a) => (
                  <li key={a.schluessel}>
                    <button
                      type="button"
                      aria-current={a.schluessel === gewaehlt ? 'true' : undefined}
                      className={`gliederungszeile ${a.schluessel === gewaehlt ? 'aktiv' : ''}`}
                      onClick={() => setGewaehlt(a.schluessel)}
                    >
                      <Zustandssymbol art={zustandssymbolart(a)} />
                      <span className="text">{a.namen[sprache] ?? a.namen.de}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )
        })}

        <p className="gliederungsfuss">
          <span>
            {t('anhang.offen')
              .replace('{offen}', String(offen))
              .replace('{alle}', String(buch.angaben.length))}
          </span>
          {hart > 0 ? (
            <strong className="blockiert">
              {t('anhang.blockieren').replace('{zahl}', String(hart))}
            </strong>
          ) : (
            <strong className="fertig">{t('anhang.blockiert.keine')}</strong>
          )}
        </p>
      </nav>

      {/* --- Mitte: die Angabe. Diese Spalte ist ein Text. --- */}
      <article className="anhangsangabe">
        {!angabe && <p className="hinweis">{t('anhang.keineangabe')}</p>}

        {angabe && (
          <>
            <p className="fundstelle">
              {angabe.fundstelle}
              {angabe.standard && <span className="standard">{angabe.standard}</span>}
            </p>
            <h2>{angabe.namen[sprache] ?? angabe.namen.de}</h2>

            <p className="merkmale">
              <span className={`marke-art ${angabe.art.toLowerCase()}`}>{t(ART[angabe.art])}</span>
              <span className={`marke-herkunft ${angabe.herkunft.toLowerCase()}`}>
                {t(HERKUNFT[angabe.herkunft])}
              </span>
              {angabe.istFehlanzeige && (
                <span className="marke-fehlanzeige" title={t('anhang.fehlanzeige.erklaerung')}>
                  {t('anhang.fehlanzeige')}
                </span>
              )}
              {angabe.verantwortlicher && (
                <span className="zusatz">
                  {t('anhang.verantwortlich')}: {angabe.verantwortlicher}
                </span>
              )}
              {angabe.frist && (
                <span className="zusatz">
                  {t('anhang.frist')}: {angabe.frist}
                </span>
              )}
            </p>

            {/* Der Text in der gewählten Sprache — oder, wo es ihn dort nicht
                gibt, in der, in der er vorliegt. Eine fehlende Übersetzung ist
                ein Zustand, keine leere Seite (R223); dass sie fehlt, sagt die
                Vollständigkeitsprüfung. */}
            {(() => {
              const vorhanden = angabe.texte[sprache] ?? angabe.texte.de
              if (!vorhanden) {
                return (
                  <div className="leerhinweis">
                    <h3>{t('anhang.leer.titel')}</h3>
                    <p>{t('anhang.leer.satz')}</p>
                  </div>
                )
              }

              return (
                <>
                  {!angabe.texte[sprache] && (
                    <p className="sprachhinweis">
                      {t('anhang.zustand.fehlt')} — {sprache.toUpperCase()}
                    </p>
                  )}
                  <Satz
                    text={vorhanden}
                    anker={angabe.anker}
                    aufNachweis={(w) => {
                      if (!w.position || !w.berichtsart || !w.spalte) return
                      aufNachweis({
                        art: w.berichtsart,
                        position: w.position,
                        positionsname: w.positionsname ?? w.position,
                        konto: w.konto,
                        einheit: w.einheit,
                        spalte: w.spalte,
                      })
                    }}
                  />
                </>
              )
            })()}
          </>
        )}
      </article>

      {/* --- Rechts: woher jede Zahl im Absatz kommt. Der Prüfer liest den
          Satz und sieht daneben, worauf er sich stützt — ohne zu klicken. --- */}
      <aside className="anhangsherkunft">
        <h2 className="abschnitt">{t('anhang.herkunft')}</h2>

        {buch.laufId === null && <p className="hinweis">{t('anhang.keinlauf')}</p>}

        {angabe?.anker.length === 0 && angabe.herkunft === 'Erklaert' && (
          <p className="hinweis">{t('anhang.herkunft.erklaert')}</p>
        )}

        {angabe?.anker.map((w) => (
          <div className="ankerkarte" key={w.marke}>
            <p className="betrag">{w.dargestellt ?? t('anhang.anker.nichtaufloesbar')}</p>

            {w.quelle === 'Lauf' ? (
              <>
                <p className="quelle">{w.positionsname ?? w.position ?? w.konto}</p>
                {/* Ohne Laufnummer (7.2 Abschnitt 36, R1142) — bis zum
                    2026-10-06 „Lauf 57 · Summe". */}
                <p className="lauf">{w.spalte}</p>
              </>
            ) : w.quelle === 'Kfr' ? (
              // Aus der Kapitalflussrechnung (R1023): was die Gegenposition in
              // der Investitionstätigkeit aufgehoben hat.
              <>
                <p className="quelle">{w.positionsname ?? w.position ?? w.konto}</p>
                <p className="lauf">{t('anhang.anker.kfr')}</p>
              </>
            ) : (
              <>
                <p className="quelle">{t('anhang.anker.auszulieferung')}</p>
                <p className="lauf">
                  {t('anhang.anker.melder')}: {w.melder.join(', ')}
                </p>
              </>
            )}

            {/* Der Vorjahreswert steht HIER und nicht im Text: Der häufigste
                Fehler ist ein übernommener Satz mit einer neuen Zahl, die nicht
                mehr zu ihm passt (2.15, Abschnitt 6.3). */}
            {w.vorjahrDargestellt && (
              <p className="vorjahr">
                {t('anhang.anker.vorjahr')}: {w.vorjahrDargestellt}
              </p>
            )}
          </div>
        ))}

        <div className="pruefcode" title={t('anhang.pruefcode.erklaerung')}>
          <p className="name">{t('anhang.pruefcode')}</p>
          <p className="wert">{buch.pruefcode.slice(0, 16)}…</p>
          <p className="name">
            {t('anhang.katalogausgabe')} {buch.katalogausgabe}
          </p>
        </div>
      </aside>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Der Angabenkatalog (R219)
   ------------------------------------------------------------------------- */

export function Angabenkatalog({ sprache }: { sprache: string }) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('anhang:angabenkatalog')

  const [eintraege, setEintraege] = useState<Angabenkatalogeintrag[] | null>(null)

  useEffect(() => {
    let gilt = true
    api.angabenkatalog().then((k) => {
      if (gilt) setEintraege(k)
    })
    return () => {
      gilt = false
    }
  }, [])

  if (!eintraege) return null

  return (
    <>

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('anhang.katalog.fundstelle')}</th>
              <th>{t('anhang.katalog.bezeichnung')}</th>
              <th>{t('anhang.katalog.art')}</th>
              <th>{t('anhang.katalog.herkunft')}</th>
              <th>{t('anhang.katalog.gruppe')}</th>
            </tr>
          </thead>
          <tbody>
            {eintraege.map((e) => (
              <tr key={e.kennung}>
                <td className="fundstelle">
                  {e.fundstelle}
                  {e.standard && <span className="standard">{e.standard}</span>}
                </td>
                <td>
                  {e.namen[sprache] ?? e.namen.de}
                  {e.schutzklausel && (
                    <span className="schutzklausel" title={t('anhang.katalog.schutzklausel')}>
                      §&nbsp;286
                    </span>
                  )}
                </td>
                <td>
                  <span className={`marke-art ${e.art.toLowerCase()}`}>{t(ART[e.art])}</span>
                </td>
                <td>
                  <span className={`marke-herkunft ${e.herkunft.toLowerCase()}`}>
                    {t(HERKUNFT[e.herkunft])}
                  </span>
                </td>
                <td>{t(GRUPPEN.find((g) => g.id === e.gruppe)!.schluessel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/* -------------------------------------------------------------------------
   Die Vollständigkeit (R231)
   ------------------------------------------------------------------------- */

export function Vollstaendigkeit({
  kreis,
  ledger,
  periode,
}: {
  kreis: string
  ledger: string
  periode: number
}) {
  const [buch, setBuch] = useState<Buch | null>(null)

  useEffect(() => {
    let gilt = true
    api.anhangsbuch(kreis, ledger, periode).then((b) => {
      if (gilt) setBuch(b)
    })
    return () => {
      gilt = false
    }
  }, [kreis, ledger, periode])

  if (!buch) return null

  const gruppen: { haerte: 'Hart' | 'Weich'; titel: Sprachschluessel }[] = [
    { haerte: 'Hart', titel: 'anhang.vollstaendigkeit.hart' },
    { haerte: 'Weich', titel: 'anhang.vollstaendigkeit.weich' },
  ]

  return (
    <>

      {buch.befunde.length === 0 && (
        <p className="hinweis">{t('anhang.vollstaendigkeit.leer')}</p>
      )}

      {gruppen.map(({ haerte, titel }) => {
        const drin: Vollstaendigkeitsbefund[] = buch.befunde.filter((b) => b.haerte === haerte)
        if (drin.length === 0) return null

        return (
          <section className="befunde" key={haerte}>
            <h2 className="abschnitt">
              {t(titel)} <span className="menge">{anzahlwort(drin.length)}</span>
            </h2>
            <ul>
              {drin.map((b, i) => (
                <li className={haerte === 'Hart' ? 'hart' : 'weich'} key={`${b.kennung}-${i}`}>
                  <Zustandssymbol art={haerte === 'Hart' ? 'blockiert' : 'offen'} />
                  <span>{b.meldung}</span>
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </>
  )
}
